/**
 *  NotificationBell.jsx
 *  @description: Campana de avisos del portal (fija arriba a la derecha).
 *  Se monta una sola vez en el router para que el stream no se reconecte en
 *  cada cambio de página. Si el servidor no tiene la migración 0005, no se
 *  muestra.
 */
import React, { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import cogoToast from 'cogo-toast';

import {
   list,
   markRead,
   markAllRead,
   sendTest,
   openStream,
   enablePush,
   pushPermission,
   NOTIFICATION_EVENT,
} from '../../core/webNotifications';

const NotificationBell = ({ history }) => {
   const [available, setAvailable] = useState(false);
   const [items, setItems] = useState([]);
   const [open, setOpen] = useState(false);
   const [permission, setPermission] = useState(pushPermission());
   const box = useRef(null);

   const go = (n) => {
      setOpen(false);
      if (!n.read) {
         setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
         markRead(n.id);
      }
      if (n.link) history.push(n.link);
   };

   useEffect(() => {
      let close = () => {};
      let alive = true;
      (async () => {
         const res = await list();
         if (!alive || !res.ok) return; // 404: la 0005 aún no está aplicada
         setAvailable(true);
         setItems(res.data || []);
         // Resincroniza la suscripción sin pedir permiso (solo si ya lo dio).
         enablePush(false).then((p) => alive && setPermission(p === 'unavailable' ? pushPermission() : p));
         close = openStream(
            (ev) => {
               if (ev.kind === 'notification' && ev.notification) {
                  const n = ev.notification;
                  setItems((prev) => (prev.some((i) => i.id === n.id) ? prev : [n, ...prev].slice(0, 50)));
                  // Para que un chat abierto se recargue solo (RequestChat).
                  window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail: n }));
                  const toast = cogoToast.info(n.title, {
                     position: 'top-right',
                     hideAfter: 6,
                     onClick: () => {
                        toast.hide();
                        go(n);
                     },
                  });
               } else if (ev.kind === 'read') {
                  setItems((prev) =>
                     prev.map((i) => (ev.all || (ev.readIds || []).includes(i.id) ? { ...i, read: true } : i))
                  );
               }
            },
            // Al reconectar se recupera lo que llegó mientras no había stream.
            async () => {
               const again = await list();
               if (alive && again.ok) setItems(again.data || []);
            }
         );
      })();

      // El service worker avisa cuando el navegador rotó la suscripción.
      const onMessage = (e) => {
         if (e.data && e.data.type === 'pushsubscriptionchange') enablePush(false);
      };
      if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onMessage);
      return () => {
         alive = false;
         close();
         if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onMessage);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (!open) return undefined;
      const onClick = (e) => {
         if (box.current && !box.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
   }, [open]);

   if (!available) return null;
   const unread = items.filter((i) => !i.read).length;

   const readAll = async () => {
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      await markAllRead();
   };

   // Llega por el mismo camino que los avisos reales: campana, stream y push.
   const test = async () => {
      const res = await sendTest();
      if (!res.ok) cogoToast.error(res.message || 'No se pudo enviar la prueba.', { position: 'top-right' });
   };

   const activate = async () => {
      const p = await enablePush(true);
      setPermission(p === 'unavailable' ? pushPermission() : p);
      if (p === 'granted') cogoToast.success('Notificaciones del navegador activadas.', { position: 'top-right' });
      else if (p === 'denied')
         cogoToast.warn('El navegador bloqueó las notificaciones. Actívalas en la configuración del sitio.', {
            position: 'top-right',
         });
      else if (p === 'unavailable')
         cogoToast.error('No se pudieron activar las notificaciones del navegador.', { position: 'top-right' });
   };

   return (
      <div className="notification-bell" ref={box}>
         <button
            type="button"
            className="bell-button"
            title="Notificaciones"
            onClick={() => setOpen(!open)}>
            <i className="fas fa-bell"></i>
            {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
         </button>
         {open && (
            <div className="bell-panel">
               <div className="bell-header">
                  <span className="weight-bold">Notificaciones</span>
                  {unread > 0 && (
                     <button type="button" className="bell-link" onClick={readAll}>
                        Marcar todas como leídas
                     </button>
                  )}
               </div>
               <div className="bell-list">
                  {items.length === 0 ? (
                     <div className="bell-empty">Sin notificaciones</div>
                  ) : (
                     items.map((n) => (
                        <button
                           type="button"
                           key={n.id}
                           className={`bell-item ${n.read ? '' : 'unread'}`}
                           onClick={() => go(n)}>
                           <i className={`fas ${n.type === 'message' ? 'fa-comment-dots' : n.type === 'test' ? 'fa-check-circle' : 'fa-clipboard-list'}`}></i>
                           <div className="bell-text">
                              <span className="bell-title">{n.title}</span>
                              {n.body && <span className="bell-body">{n.body}</span>}
                              <span className="bell-date">{moment(n.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                           </div>
                        </button>
                     ))
                  )}
               </div>
               {permission === 'default' && (
                  <div className="bell-footer">
                     <button type="button" className="btn btn-primary btn-small color-white" onClick={activate}>
                        <i className="fas fa-desktop"></i>&nbsp; Activar notificaciones del navegador
                     </button>
                  </div>
               )}
               {permission === 'granted' && (
                  <div className="bell-footer">
                     <button type="button" className="bell-link" onClick={test}>
                        Enviar notificación de prueba
                     </button>
                  </div>
               )}
               {permission === 'denied' && (
                  <div className="bell-footer color-gray font-mini">
                     Las notificaciones del navegador están bloqueadas para este sitio.
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

export default withRouter(NotificationBell);
